import * as React from 'react';
import { AddressDTO, BuyerCustomerDTO, Res } from 'core/api';
import { Command } from 'pos/common';
import { CustomerFacade } from 'pos/facades';
import { ActionButtonDefinition, PosScreenClassComponent, ScreenSize } from 'pos/ui';
import * as Images from 'pos/Images';
import DeliveryAddresses from './DeliveryAddresses';
import createDeliveryAddress from '../../main/phone-orders/create-delivery-address';
import { isBillingAddress } from './helpers';
import { Flex } from 'core/ui';
import { Message, Modal } from 'core/ui-next/modal';

type State = {
	selectedAddress: Nullable<AddressDTO>;
	addresses: AddressDTO[];
};
type Props = {
	addresses: AddressDTO[];
	currentAddress: Nullable<AddressDTO>;
	customer: BuyerCustomerDTO;
} & Modal.Actions<AddressDTO>;

class ChangeDeliveryAddressScreen extends PosScreenClassComponent<Props, State> {

	protected getScreenId(): string {
		return 'CHANGE_DELIVERY_ADDRESS';
	}

	protected getTitle(): string {
		return Res.Web.SELECT_DELIVERY_ADDRESS();
	}

	protected getSize(): ScreenSize {
		return 'medium';
	}

	protected useKeypad(): boolean {
		return false;
	}

	protected getImage(): string | SvgIcon {
		return Images.Address;
	}

	protected getActionButtonsDefs(): Nullable<ActionButtonDefinition>[] {
		return this.okCancelButtons({
			onOK: async () => {
				if (this.state.selectedAddress !== null)
					this.close(this.state.selectedAddress);
			}
		});
	}

	createInitialState(props: Props): Nullable<State> {
		return { selectedAddress: props.currentAddress, addresses: props.addresses };
	}
	onComponentDidMount(): void {
	}
	onComponentWillUnmount(): void {
	}

	onAddDeliveryAddress = async (): Promise<void> => {
		const result = await createDeliveryAddress({ Id: this.props.customer.Id });
		if (result.ok) {
			const newAddresses = [this.props.customer.Address, ...await CustomerFacade.getDeliveryAddresses(this.props.customer.Id)];
			this.setState({ addresses: newAddresses, selectedAddress: newAddresses.last() });
		}
	};

	onRemoveDeliveryAddress = async (address: AddressDTO): Promise<void> => {
		if (isBillingAddress(this.props.customer.Address, address)) {
			await Message.show(Res.Msg.MSG_CANNOT_DELETE_BILLING_ADDRESS());
			return;
		}

		const result = await Message.confirm(Res.Msg.MSG_CONFIRM_DELIVERY_ADDRESS_DELETION());
		if (result.ok) {
			await CustomerFacade.removeDeliveryAddress(this.props.customer.Id, address);
			const newAddresses = [this.props.customer.Address, ...await CustomerFacade.getDeliveryAddresses(this.props.customer.Id)];
			this.setState({ addresses: newAddresses, selectedAddress: newAddresses.last() });
		}
	};

	protected getContent(): React.ReactNode {
		return <Flex.Vertical>
			<Flex.Item className='m-b-l m-l m-t'>
				<h4>
					<span className='font-bold'>{Res.Web.SELECT_DELIVERY_ADDRESS_CURRENT_ADDRESS_LBL()} </span>
					{
						this.state.selectedAddress
							? `${this.state.selectedAddress.Street} - ${this.state.selectedAddress.ZipCode} ${this.state.selectedAddress.City} ${this.state.selectedAddress.Region}`
							: ''
					}
				</h4>

			</Flex.Item>
			<Flex.Item size='*' className='m'>
				<DeliveryAddresses currentAddress={this.state.selectedAddress}
					deliveryAddresses={this.state.addresses}
					onAddDeliveryAddress={this.onAddDeliveryAddress}
					onSelectDeliveryAddress={(address) => this.setState({ selectedAddress: address })}
					onRemoveDeliveryAddress={this.onRemoveDeliveryAddress} />
			</Flex.Item>
		</Flex.Vertical>;
	}

}

declare global {
	interface CommandType {
		changeDeliveryAddress: (customer: BuyerCustomerDTO, currentAddress: Nullable<AddressDTO>) => Promise<Result<AddressDTO>>
	}
}

Command.register('changeDeliveryAddress', 'NO_PRECONDITION', async (customer: BuyerCustomerDTO, currentAddress: Nullable<AddressDTO>) => {
	const addresses = await CustomerFacade.getDeliveryAddresses(customer.Id);

	return await Modal.show(ChangeDeliveryAddressScreen, { addresses: [customer.Address, ...addresses], currentAddress: currentAddress, customer: customer });
});
